/**
 * «Взять на вахту» на странице «Мой день» (МП-13, МП-16 в браузере): одной кнопкой сохранить на телефон план дня,
 * открытые замечания и шаблоны чек-листов; видно, что сохранено, когда, сколько места занято и сколько записей
 * ещё не отправлено. Без связи страница открывается из сохранённой копии (public/sw.js).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Checkbox } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { registerOfflineShell } from './offline/register-sw';
import {
  deviceStorage,
  formatSize,
  hasSavedCopy,
  isStale,
  mayRefreshNow,
  networkKind,
  readWatchMeta,
  readWatchSettings,
  takeOnWatch,
  WATCH_PARTS,
  type WatchMeta,
  type WatchPart,
  type WatchSettings,
  writeWatchSettings,
} from './offline/watch';

const when = (t: number) =>
  new Date(t).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function WatchPanel({
  scope,
  projectIds,
  pending,
}: {
  scope: { tenantId: string; userId: string };
  projectIds: string[];
  /** Записи на телефоне, которые ещё не ушли (замечания и приёмки) */
  pending: number;
}) {
  const [settings, setSettings] = useState<WatchSettings>(() => readWatchSettings(scope));
  const [meta, setMeta] = useState<WatchMeta | null>(() => readWatchMeta(scope));
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [space, setSpace] = useState<{ used: number; quota: number } | null>(null);
  const [open, setOpen] = useState(false);
  const autoDone = useRef(false);
  const alive = useRef(true);
  const today = new Date().toLocaleDateString('en-CA');
  const ids = projectIds.join(',');

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Отметка без копии (после выхода кэш стёрт обработчиком) ничего не обещает: сбрасываем её
  useEffect(() => {
    void hasSavedCopy().then((has) => {
      if (!has) setMeta(null);
    });
  }, []);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: объём места перечитывается после каждого сохранения
  useEffect(() => {
    void deviceStorage().then(setSpace);
  }, [meta]);

  const run = async (quiet: boolean) => {
    if (busy || !settings.parts.length) return;
    setBusy(true);
    try {
      const shell = await registerOfflineShell();
      if (shell !== 'ready' || !navigator.serviceWorker?.controller) {
        if (!quiet) toast('Этот браузер не умеет хранить данные для работы без связи', 'danger');
        return;
      }
      const r = await takeOnWatch(
        scope,
        settings,
        ids ? ids.split(',').map((id) => ({ id })) : [],
        today,
        undefined,
        undefined,
        () => alive.current,
      );
      setMeta(r.files > 0 ? r : readWatchMeta(scope));
      if (!quiet)
        toast(
          r.files > 0
            ? `Сохранено на телефон: ${formatSize(r.bytes)}${r.failed ? `, не взялось: ${r.failed}` : ''}`
            : 'Ничего не сохранилось — проверьте связь',
          r.files > 0 ? 'success' : 'danger',
        );
    } finally {
      setBusy(false);
    }
  };

  // Уже взятое на вахту обновляется само при открытии страницы: по Wi-Fi сразу, по мобильной сети — если разрешено
  // biome-ignore lint/correctness/useExhaustiveDependencies: один раз на открытие страницы, когда известны проекты
  useEffect(() => {
    if (autoDone.current || !meta || !ids) return;
    if (!isStale(meta, Date.now()) || !mayRefreshNow(settings, navigator.onLine, networkKind())) return;
    autoDone.current = true;
    void run(true);
  }, [ids]);

  const toggle = (part: WatchPart, on: boolean) => {
    const next = {
      ...settings,
      parts: on ? [...settings.parts, part] : settings.parts.filter((p) => p !== part),
    };
    setSettings(next);
    writeWatchSettings(scope, next);
  };
  const setMobile = (allowMobile: boolean) => {
    const next = { ...settings, allowMobile };
    setSettings(next);
    writeWatchSettings(scope, next);
  };

  return (
    <Panel
      title="Взять на вахту"
      meta={meta ? `сохранено ${when(meta.savedAt)}` : 'не сохранено'}
      className="mb-4"
    >
      {!online ? (
        <p className="mb-3 rounded-md border border-line bg-surface-2 p-3 text-[14px] text-ink">
          Связи нет. Открыта сохранённая копия{meta ? ` от ${when(meta.savedAt)}` : ' (если она есть)'}. Новые
          записи сохраняются на телефоне и уйдут сами.
        </p>
      ) : null}
      <p className="text-[14px] text-ink-2">
        {meta
          ? `На телефоне: ${formatSize(meta.bytes)}, файлов ${meta.files}. Не отправлено записей: ${pending}.`
          : `Скачайте на телефон то, что нужно на смену, — потом всё откроется без связи. Не отправлено записей: ${pending}.`}
        {space ? ` Занято в браузере ${formatSize(space.used)} из ${formatSize(space.quota)}.` : ''}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="primary"
          className="min-h-11"
          disabled={busy || !online || !settings.parts.length || !projectIds.length}
          onClick={() => void run(false)}
        >
          {busy ? 'Сохраняю…' : meta ? 'Обновить сейчас' : 'Взять на вахту'}
        </Button>
        <Button type="button" variant="ghost" className="min-h-11" onClick={() => setOpen(!open)}>
          {open ? 'Скрыть выбор' : 'Что брать'}
        </Button>
      </div>
      {open ? (
        <div className="mt-3 flex flex-col">
          {WATCH_PARTS.map((p) => (
            <Checkbox
              key={p.value}
              className="min-h-11 items-center"
              label={p.label}
              description={p.hint}
              checked={settings.parts.includes(p.value)}
              onChange={(e) => toggle(p.value, e.target.checked)}
            />
          ))}
          <Checkbox
            className="min-h-11 items-center"
            label="Обновлять и по мобильной сети"
            description="Без галочки сохранённое обновляется только по Wi-Fi"
            checked={settings.allowMobile}
            onChange={(e) => setMobile(e.target.checked)}
          />
        </div>
      ) : null}
    </Panel>
  );
}
